import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
	SimpleChange,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export type DebugTab = 'preview' | 'source' | 'result';
type ViewMode = 'pretty' | 'raw';
type JsonTab = 'source' | 'result' | 'preview';

type ParseSuccess = { ok: true; value: unknown };
type ParseFailure = { ok: false; error: string };
type ParseResult = ParseSuccess | ParseFailure;

@Component({
  selector: 'app-mapping-debug-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './mapping-debug-panel.component.html',
  styleUrls: ['./mapping-debug-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MappingDebugPanelComponent implements OnChanges {
  @Input() sourceJson: string | object | null = '';
  @Input() resultJson: string | object | null = '';
  @Input() previewJson: string | object | null = '';

  isOpen = true;
  activeTab: DebugTab = 'preview';

  readonly tabs: { key: DebugTab; label: string }[] = [
    { key: 'preview', label: 'Preview log' },
    { key: 'source', label: 'Source payload' },
    { key: 'result', label: 'Mapped result' }
  ];

  readonly viewModes: Record<JsonTab, ViewMode> = {
    source: 'pretty',
    result: 'pretty',
    preview: 'pretty'
  };

  readonly rawDrafts: Record<JsonTab, string> = {
    source: '',
    result: '',
    preview: ''
  };

  readonly formattedSnapshots: Record<JsonTab, string> = {
    source: '',
    result: '',
    preview: ''
  };

  readonly parseErrors: Partial<Record<JsonTab, string>> = {};
	private readonly inputValues: Partial<Record<JsonTab, unknown>> = {};
	private readonly dirtyTabs = new Set<JsonTab>();

  ngOnChanges(changes: SimpleChanges): void {
		this.captureInputChange('source', changes['sourceJson']);
		this.captureInputChange('result', changes['resultJson']);
		this.captureInputChange('preview', changes['previewJson']);
		this.materializeTab(this.activeTab);
  }

  togglePanel(): void {
    this.isOpen = !this.isOpen;
		if (this.isOpen) {
			this.materializeTab(this.activeTab);
		}
  }

  setTab(tab: DebugTab): void {
    this.activeTab = tab;
		this.materializeTab(tab);
  }

  trackByKey(_: number, item: { key: DebugTab; label: string }): string {
    return item.key;
  }

  setViewMode(tab: JsonTab, mode: ViewMode): void {
    this.viewModes[tab] = mode;
  }

  format(tab: JsonTab): void {
    this.rawDrafts[tab] = this.tryPrettyFormat(this.rawDrafts[tab]);
    this.updateFormattedSnapshot(tab);
    this.viewModes[tab] = 'pretty';
  }

  async copy(text: string): Promise<void> {
    const value = text ?? '';
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(textarea);
      }
    }
  }

  onRawChanged(tab: JsonTab, value: string): void {
    this.rawDrafts[tab] = value ?? '';
    this.updateFormattedSnapshot(tab);
  }

  getDisplayText(tab: JsonTab): string {
    return this.viewModes[tab] === 'raw'
      ? this.rawDrafts[tab]
      : this.formattedSnapshots[tab];
  }

  getParseError(tab: JsonTab): string | null {
    return this.parseErrors[tab] ?? null;
  }

  hasContent(tab: JsonTab): boolean {
    return !!(this.rawDrafts[tab] ?? '').trim();
  }

	private captureInputChange(tab: JsonTab, change: SimpleChange | undefined): void {
		if (!change) {
			return;
		}

		this.inputValues[tab] = change.currentValue;
		this.dirtyTabs.add(tab);
	}

	private materializeTab(tab: JsonTab): void {
		if (!this.dirtyTabs.has(tab)) {
			return;
		}

		this.syncTabFromInput(tab, this.inputValues[tab]);
		this.dirtyTabs.delete(tab);
	}

  private syncTabFromInput(tab: JsonTab, value: unknown): void {
    if (value === undefined) {
      return;
    }

    const normalized = this.normalizeJsonInput(value);
    this.rawDrafts[tab] = normalized;
		if (typeof value === 'string') {
			this.updateFormattedSnapshot(tab);
			return;
		}

		this.formattedSnapshots[tab] = normalized;
		delete this.parseErrors[tab];
  }

  private isParseFailure(result: ParseResult): result is ParseFailure {
    return result.ok === false;
  }

  private updateFormattedSnapshot(tab: JsonTab): void {
    const rawValue = this.rawDrafts[tab] ?? '';
    const parsed = this.tryParse(rawValue);

    if (this.isParseFailure(parsed)) {
      this.formattedSnapshots[tab] = rawValue;
      this.parseErrors[tab] = parsed.error;
      return;
    }

    this.formattedSnapshots[tab] = JSON.stringify(parsed.value, null, 2);
    delete this.parseErrors[tab];
  }

  private normalizeJsonInput(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (value === null || value === undefined) {
      return '';
    }

    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  private tryPrettyFormat(text: string): string {
    const parsed = this.tryParse(text ?? '');
    return parsed.ok ? JSON.stringify(parsed.value, null, 2) : text ?? '';
  }

  private tryParse(text: string): ParseResult {
    const value = (text ?? '').trim();

    if (!value) {
      return { ok: true, value: {} };
    }

    try {
      return { ok: true, value: JSON.parse(value) };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON';
      return { ok: false, error: message };
    }
  }
}
